/* @flow */
import invariant from 'invariant';
import * as React from 'react';

import createChainedFunction from 'utils/createChainedFunction';
import KeyConstants from 'constants/KeyConstants';
import Layer from 'components/Layer';
import LayerTrigger from 'components/LayerTrigger';
import List from 'components/List';
import LoadingIndicator from 'components/LoadingIndicator';
import Text from 'components/Text';
import { ALL_TOKENS_ITEM, HEADER_ITEM_ID } from 'components/Tokenizer';
import { compact } from 'lodash';

import type { TokenItem, HeaderItem } from 'components/Tokenizer';
// eslint-disable-next-line no-unused-vars
import type { Element } from 'react';

class Typeahead extends React.Component<
  {
    results: *[],
    query: string,
    // Function that renders the list of results
    listRenderer: (*, boolean) => React.Element<any>,
    // Function that renders a result
    itemRenderer: (TokenItem, boolean, string, Function) => React.Element<any>,
    // Function that renders a "Select all" item
    selectAllItemRenderer?: (boolean, Function) => React.Element<any>,
    // Function that renders a no more items prompt
    noMoreItemsRenderer: void => ?React.Element<any>,
    // Function that renders a no results prompt
    noResultsRenderer: string => ?React.Element<any>,
    // Function that renders a section header
    headerRenderer?: HeaderItem => React$Element<*>,
    // Show the list of results inline rather than in a layer
    inline: boolean,
    // Callback when an item is selected
    onSelect: TokenItem => void,
    // Callback when the "Select all" item is selected
    onSelectAll: () => void,
    // Hide the results list
    hideResults: boolean,
    onHighlightIndexChange: number => void,
    onEnter: void => void,
    className?: ?string,
    children?: React$Element<*>,
    loading?: boolean,
    manageLayer: boolean,
    useCustomSelectionHandler: boolean,
  },
  { highlightIndex: number }
> {
  static defaultProps = {
    listRenderer: (items: TokenItem[], loading: boolean) => {
      return (
        <List>
          {items}
          {loading && <LoadingIndicator />}
        </List>
      );
    },
    itemRenderer: (item: TokenItem, highlighted: boolean, query: string, onSelect: () => void) => {
      return (
        <Text onClick={onSelect} weight={highlighted ? 'bold' : 'normal'}>
          {item}
        </Text>
      );
    },
    noMoreItemsRenderer: () => null,
    noResultsRenderer: (_str: string) => null,
    hideResults: false,
    onHighlightIndexChange: (_index: number) => undefined,
    onEnter: () => undefined,
    manageLayer: true,
    useCustomSelectionHandler: false,
  };

  state = {
    highlightIndex: -1,
  };

  componentDidMount() {
    if (!this.props.inline) {
      // Hide the layer on window resize
      window.addEventListener('resize', this.hideLayer.bind(this), false);
    }
  }

  componentWillReceiveProps(nextProps: Object) {
    if (this.props.query !== nextProps.query) {
      if (nextProps.query === '') {
        this.setState({ highlightIndex: -1 });
      } else {
        this.setState({ highlightIndex: 0 });
      }
    }
  }

  shouldComponentUpdate(nextProps: Object, nextState: Object) {
    const propsChanged =
      this.props.query !== nextProps.query ||
      this.props.results.length !== nextProps.results.length ||
      this.props.children !== nextProps.children;
    const stateChanged = this.state.highlightIndex !== nextState.highlightIndex;
    return propsChanged || stateChanged;
  }

  componentDidUpdate(prevProps: Object, prevState: Object) {
    if (this.refs.trigger) {
      this.refs.trigger.updateLayerPosition();
    }
    if (this.state.highlightIndex !== prevState.highlightIndex) {
      this.props.onHighlightIndexChange(this.state.highlightIndex);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.hideLayer.bind(this), false);
  }

  handleKeyDown(event: SyntheticKeyboardEvent<>) {
    const { keyCode } = event;
    switch (keyCode) {
      case KeyConstants.DOWN:
      case KeyConstants.UP: {
        const { highlightIndex } = this.state;
        if (keyCode === KeyConstants.UP) {
          this.setState({
            highlightIndex: highlightIndex > 0 ? highlightIndex - 1 : this.props.results.length - 1,
          });
        } else {
          this.setState({
            highlightIndex: highlightIndex + 1 < this.props.results.length ? highlightIndex + 1 : 0,
          });
        }
        this.showLayer();
        break;
      }
      case KeyConstants.ENTER:
        if (this.state.highlightIndex >= 0) {
          this.selectItem(this.props.results[this.state.highlightIndex]);
        } else {
          this.props.onEnter();
        }
        break;
      default:
        this.showLayer();
        break;
    }
  }

  handleInputFocus() {
    setTimeout(() => this.showLayer(), 0);
  }

  showLayer() {
    if (!this.refs.trigger || this.refs.trigger.isLayerShown()) {
      return;
    }
    this.refs.trigger.showLayer();
  }

  hideLayer() {
    if (!this.refs.trigger || !this.refs.trigger.isLayerShown()) {
      return;
    }
    this.refs.trigger.hideLayer();
    this.resetHighlight();
  }

  resetHighlight() {
    if (this.state.highlightIndex >= 0) {
      this.setState({ highlightIndex: -1 });
    }
  }

  selectItem(datum: TokenItem) {
    this.resetHighlight();
    this.props.onSelect(datum);
  }

  selectAll() {
    this.resetHighlight();
    this.props.onSelectAll();
  }

  renderInput() {
    const input = React.Children.only(this.props.children);

    return React.cloneElement(input, {
      onFocus: createChainedFunction(() => this.handleInputFocus(), input.props.onFocus),
    });
  }

  renderList() {
    const { query, hideResults } = this.props;
    if (hideResults) {
      return null;
    }

    const items = this.props.results.map((datum: TokenItem, index) => {
      const item = this.renderListItem(datum, index === this.state.highlightIndex, query);
      return React.cloneElement(item, {
        key: datum.id,
        onMouseEnter: createChainedFunction(() => {
          if (index !== this.state.highlightIndex) {
            this.setState({ highlightIndex: index });
          }
        }, item.props.onMouseEnter),
        onMouseLeave: createChainedFunction(() => {
          if (index === this.state.highlightIndex) {
            this.setState({ highlightIndex: -1 });
          }
        }, item.props.onMouseLeave),
      });
    });

    if (items.length === 0) {
      items.push(query ? this.props.noResultsRenderer(query) : this.props.noMoreItemsRenderer());
    }

    return this.props.listRenderer(compact(items), this.props.loading || false);
  }

  renderListItem(
    item: TokenItem | HeaderItem,
    highlighted: boolean,
    query: string
  ): React.Element<any> {
    const {
      selectAllItemRenderer,
      headerRenderer,
      useCustomSelectionHandler,
      itemRenderer,
      onSelect,
    } = this.props;

    if (typeof item.id === 'string') {
      if (item === ALL_TOKENS_ITEM) {
        invariant(selectAllItemRenderer, 'ALL_TOKENS_ITEM without renderer');
        return selectAllItemRenderer(highlighted, () => this.selectAll());
      } else if (item.id.startsWith(HEADER_ITEM_ID)) {
        invariant(headerRenderer, 'HEADER_ITEM without renderer');
        // $FlowFixMe 'type' property not found?
        const headerItem = (item: HeaderItem);
        return headerRenderer(headerItem);
      }
    }

    const selectionHandler = useCustomSelectionHandler ? onSelect : () => this.selectItem(item);
    return itemRenderer(item, highlighted, query, selectionHandler);
  }

  renderLayer() {
    const { manageLayer } = this.props;
    return (
      <Layer
        hideOnBlur={manageLayer}
        style={{ margin: '0 44px' }}
        onMouseLeave={() => this.resetHighlight()}
        onRequestHide={() => this.resetHighlight()}>
        {this.renderList()}
      </Layer>
    );
  }

  render() {
    const props = { ...this.props, onSelect: null };
    const { inline, className, manageLayer } = props;

    if (inline) {
      return (
        <div className={className}>
          {this.renderInput()}
          {this.renderList()}
        </div>
      );
    }

    return (
      <LayerTrigger
        ref="trigger"
        className={className}
        layer={this.renderLayer()}
        trigger="manual"
        placement="bottom"
        useContextWidth
        fixedPlacement={!manageLayer}>
        {this.renderInput()}
      </LayerTrigger>
    );
  }
}

export default Typeahead;
